import { isNil } from '@aloft/toolkit-shared';
import { Block, Flex, Input, InputType, SizeProperty, Typography } from '@aloft/toolkit-sui';

import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import {
  ALERT_TARGET_FIELD_TITLE,
  getAlertTargetInputFieldSx,
} from '../../../components/PropertyGrid/fields/styles.ts';
import { setFocusedOrderDetailsFieldKey } from '../../../redux/features/uiSlice.ts';
import { useAppDispatch } from '../../../redux/hooks.ts';
import { useOrderDetailsFieldAlertHighlight } from '../useOrderDetailsFieldAlertHighlight.ts';

const isUnsetValue = (value: unknown) =>
  isNil(value) || value === '' || (typeof value === 'string' && value.trim() === '');

export const OrderDetailsInput = <TFieldValues extends FieldValues>({
  control,
  placeholderText,
  type = 'text',
  isRequired = false,
  width = '250px',
  name,
  label,
  errorMessage,
  onValueChange,
  isDisabled,
}: {
  control: Control<TFieldValues>;
  placeholderText?: string;
  type?: InputType | 'aloftPrice';
  isRequired?: boolean;
  width?: SizeProperty;
  name: Path<TFieldValues>;
  label: string;
  errorMessage?: string;
  onValueChange?: (value: unknown) => void;
  isDisabled?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { isAlertTarget, isAlertHighlighted } = useOrderDetailsFieldAlertHighlight(name);

  return (
    <Flex data-order-details-field={name}>
      <Block width="250px">
        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
          <Typography variant="h4" color="textSecondary">
            {label}
          </Typography>
          {isRequired && <span style={{ color: 'red' }}> *</span>}
        </div>
      </Block>
      <Controller
        name={name}
        control={control}
        rules={{
          required: isRequired,
        }}
        render={({ field, fieldState }) => {
          const interacted = fieldState.isDirty || fieldState.isTouched;
          const formError = fieldState.error?.message;
          const customError = interacted ? errorMessage : undefined;
          const hasError = formError || customError;
          const missingRequiredWhileInteracted =
            isRequired && interacted && isUnsetValue(field.value);
          const isInvalid = !!hasError || missingRequiredWhileInteracted;
          const alertTargetSx = getAlertTargetInputFieldSx(
            isAlertHighlighted,
            'error',
            isAlertHighlighted,
          );

          return (
            <Block width={width}>
              <Input
                size="sm"
                type={type as any}
                placeholderText={placeholderText}
                isInvalid={isInvalid}
                title={isAlertHighlighted ? ALERT_TARGET_FIELD_TITLE : undefined}
                __dangerDoNotUseSx={alertTargetSx}
                {...field}
                onFocus={() => {
                  if (isAlertTarget) {
                    dispatch(setFocusedOrderDetailsFieldKey(undefined));
                  }
                }}
                onChange={(value: unknown) => {
                  field.onChange(value);
                  onValueChange?.(value);
                }}
                onClear={() => {
                  if (type === 'aloftPrice' || type === 'aloftDate' || type === 'aloftArea') {
                    field.onChange(undefined);
                    onValueChange?.(undefined);
                    return;
                  }

                  field.onChange('');
                  onValueChange?.('');
                }}
                isRequired={isRequired}
                isDisabled={isDisabled}
              />
              {!!(hasError || missingRequiredWhileInteracted) && (
                <Typography variant="body2" color="error">
                  {formError || customError || 'Required'}
                </Typography>
              )}
            </Block>
          );
        }}
      />
    </Flex>
  );
};
